/**
 * Auth context — Azure Static Web Apps EasyAuth (/.auth/me) plus DRA's own
 * DB-backed roles and page grants (GET /api/v1/auth/profile).
 *
 * The SWA gateway (or the SWA CLI emulator locally) handles the Entra ID
 * login and injects the client principal; this context reads that AND then
 * asks the API who the user is in `[Dra].UserRoles` terms — until P7 the
 * component tree only ever saw the raw SWA principal and never the DB
 * roles, so every permission check was unimplementable.
 *
 * THE PROFILE IS FETCHED ONCE PER PAGE LOAD, and that is the second of the two
 * staleness layers P13 had to close. The server cache is the first (5 min TTL,
 * dropped by `permissionCache.invalidateUser` / `invalidateAll` on every
 * permission change). This one is longer-lived and entirely client-side: an
 * Admin who removes their own role would keep the full sidebar until they
 * reloaded, and clicking any of it would produce 403s with no explanation.
 * `refreshProfile()` is the fix — Settings.tsx calls it after an edit that
 * touches the signed-in user. For OTHER people the change lands on their next
 * page load, which the UI says out loud rather than implying it is instant.
 */

import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { api } from '../services/api';
import type { UserProfile } from '../types/auth';

interface ClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
}

interface AuthState {
  /** Raw SWA principal — kept for e-mail display / logout, not for permission checks. */
  principal: ClientPrincipal | null;
  /** DB roles + accessible pages. null while loading, or if /.auth/me had no principal. */
  user: UserProfile | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  /** Re-read GET /api/v1/auth/profile. Call after changing the signed-in user's own roles. */
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  principal: null,
  user: null,
  loading: true,
  login: () => {},
  logout: () => {},
  refreshProfile: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState<ClientPrincipal | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/.auth/me')
      .then(res => (res.ok ? res.json() : null))
      .then(async data => {
        const swaPrincipal: ClientPrincipal | null = data?.clientPrincipal ?? null;
        setPrincipal(swaPrincipal);
        if (!swaPrincipal) {
          setUser(null);
          return;
        }
        const profile = await api.get<UserProfile>('/api/v1/auth/profile');
        setUser(profile);
      })
      .catch(() => {
        setPrincipal(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  /**
   * A failed refresh deliberately leaves the previous profile in place rather
   * than clearing it: `user === null` is the signed-out state, and a transient
   * network error must not log somebody out of the shell.
   */
  const refreshProfile = useCallback(async () => {
    try {
      const profile = await api.get<UserProfile>('/api/v1/auth/profile');
      setUser(profile);
    } catch {
      /* keep the last known profile */
    }
  }, []);

  const login = () => {
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname);
  };

  const logout = () => {
    window.location.href = '/.auth/logout';
  };

  return (
    <AuthContext.Provider value={{ principal, user, loading, login, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  return useContext(AuthContext);
}
